import { Injectable, inject } from '@angular/core';
import { CountryLanguageCodes } from '@nx-customer-apps/shared/enums';
import { FormToolsService, LocaleService } from '../../../services';
import { EnumDictionary } from '@nx-customer-apps/shared/utils';
import { LodashService } from '@nx-customer-apps/shared/services';
import { TranslateService } from '@ngx-translate/core';
import { NgxMaskPipe } from 'ngx-mask';

@Injectable({
    providedIn: 'root'
})
export class UnitConversionService {
    private localeService = inject(LocaleService);
    private formToolsService = inject(FormToolsService);
    private translateService = inject(TranslateService);

    private maskPipe: NgxMaskPipe;
    private localeToDistanceUnit: EnumDictionary<CountryLanguageCodes | string, string> = {
        ['en-GB']: 'MEASUREMENT_UNITS.MILES',
        [CountryLanguageCodes.enDE]: 'MEASUREMENT_UNITS.KM',
        [CountryLanguageCodes.deDE]: 'MEASUREMENT_UNITS.KM',
        [CountryLanguageCodes.deAT]: 'MEASUREMENT_UNITS.KM',
        [CountryLanguageCodes.deCH]: 'MEASUREMENT_UNITS.KM',
        [CountryLanguageCodes.csCZ]: 'MEASUREMENT_UNITS.KM',
        [CountryLanguageCodes.esES]: 'MEASUREMENT_UNITS.KM',
        [CountryLanguageCodes.frBE]: 'MEASUREMENT_UNITS.KM',
        [CountryLanguageCodes.frCH]: 'MEASUREMENT_UNITS.KM',
        [CountryLanguageCodes.frFR]: 'MEASUREMENT_UNITS.KM',
        [CountryLanguageCodes.nlBE]: 'MEASUREMENT_UNITS.KM',
        [CountryLanguageCodes.plPL]: 'MEASUREMENT_UNITS.KM',
        [CountryLanguageCodes.skSK]: 'MEASUREMENT_UNITS.KM'
    };
    private localeToValueConversionFactor: EnumDictionary<CountryLanguageCodes | string, number> = {
        // Example value for miles:
        ['en-GB']: 0.62,
        // Standard values for kilometers
        [CountryLanguageCodes.enDE]: 1,
        [CountryLanguageCodes.deDE]: 1,
        [CountryLanguageCodes.deAT]: 1,
        [CountryLanguageCodes.deCH]: 1,
        [CountryLanguageCodes.csCZ]: 1,
        [CountryLanguageCodes.esES]: 1,
        [CountryLanguageCodes.frBE]: 1,
        [CountryLanguageCodes.frCH]: 1,
        [CountryLanguageCodes.frFR]: 1,
        [CountryLanguageCodes.nlBE]: 1,
        [CountryLanguageCodes.plPL]: 1,
        [CountryLanguageCodes.skSK]: 1
    };

    constructor() {
        this.maskPipe = new NgxMaskPipe();
    }

    /**
     * Converts kWh/100km into kWh/100mi if it's required by locale.
     * @param value expressed in kWh/ 100km
     * @returns energy consumption per 100 km or miles by locale.
     */
    public consumptionPerDistanceByLocale(value: number | undefined | null): unknown {
        if (LodashService.isNil(value)) {
            return;
        }
        const convertedValue = Math.round(value! / this.localeToValueConversionFactor[this.localeService.locale as CountryLanguageCodes]);
        const maskedValue = this.maskValue(convertedValue);
        return `${maskedValue} ${this.translateService.instant('MEASUREMENT_UNITS.KWH')}/100 ${this.translateService.instant(
            this.localeToDistanceUnit[this.localeService.locale as CountryLanguageCodes]
        )}`;
    }

    public distancePerYearByLocale(value: number | undefined | null): unknown {
        if (LodashService.isNil(value)) {
            return;
        }
        const convertedValue = Math.round(value! * this.localeToValueConversionFactor[this.localeService.locale as CountryLanguageCodes]);
        const maskedValue = this.maskValue(convertedValue);
        return `${maskedValue} ${this.translateService.instant(
            this.localeToDistanceUnit[this.localeService.locale as CountryLanguageCodes]
        )}/${this.translateService.instant('MEASUREMENT_UNITS.YEAR')}`;
    }

    private maskValue(value: number): string {
        const maskConfig = this.formToolsService.getNumericMask();
        return this.maskPipe.transform(value, maskConfig.pattern);
    }
}
